import type { Facet } from "../api/types";
import type { Filters } from "../filters";

type Props = {
  filters: Filters;
  years: Facet[];
  months: Facet[];
  onChange: (patch: Partial<Filters>) => void;
};

/**
 * The date, asked about two ways.
 *
 * By default a **year** and a **month**, separate: month on its own means "any
 * September" across every year, and pairing it with a year picks out one exact
 * month. Both carry counts, and the month counts are for the year being looked
 * at -- a count across all years says September has 900 when the year you are
 * in has three.
 *
 * The button swaps those for a **from / to** span of month-years, either end
 * optional. The span ends carry no counts, deliberately: a release dated only
 * to a year would have to be counted in all twelve of its months, which would
 * read as twelve times the stock.
 */
export function DateFilter({ filters, years, months, onChange }: Props) {
  const f = filters;

  // Every month of a year can bound a span, so the options are built from the
  // years the data holds rather than from a fixed range.
  const spanOptions: { value: string; label: string }[] = [];
  const sortedYears = [...years].sort((a, b) => Number(b.value) - Number(a.value));
  for (const y of sortedYears) {
    for (let m = 12; m >= 1; m--) {
      const mm = String(m).padStart(2, "0");
      spanOptions.push({
        value: `${y.value}-${mm}`,
        label: `${MONTHS[m - 1]} ${y.value}`,
      });
    }
  }

  return (
    <>
      <button
        type="button"
        className={`rangebtn${f.ranging ? " on" : ""}`}
        title={f.ranging ? "back to a year and a month" : "pick a from / to span instead"}
        onClick={() =>
          // Switching drops the pair being hidden, so a filter that is no
          // longer on screen cannot still be narrowing the results.
          onChange(
            f.ranging
              ? { ranging: false, from: "", to: "" }
              : { ranging: true, year: "", month: "" },
          )
        }
      >
        {f.ranging ? "Year / month" : "Date range"}
      </button>

      {f.ranging ? (
        <div className="daterange on">
          <select
            value={f.from}
            onChange={(e) => onChange({ from: e.target.value })}
            title="from this month onwards"
          >
            <option value="">From: any</option>
            {spanOptions.map((o) => (
              // A backwards span is not a filter anyone means, so the options
              // that would build one are disabled rather than left to return
              // nothing.
              <option key={o.value} value={o.value} disabled={!!f.to && o.value > f.to}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={f.to}
            onChange={(e) => onChange({ to: e.target.value })}
            title="up to and including this month"
          >
            <option value="">To: any</option>
            {spanOptions.map((o) => (
              <option key={o.value} value={o.value} disabled={!!f.from && o.value < f.from}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="ymfields">
          <select value={f.year} onChange={(e) => onChange({ year: e.target.value })}>
            <option value="">Any year</option>
            {sortedYears.map((y) => (
              <option key={y.value} value={y.value}>
                {y.label} ({y.count})
              </option>
            ))}
          </select>
          <select value={f.month} onChange={(e) => onChange({ month: e.target.value })}>
            <option value="">Any month</option>
            {months.map((m) => (
              // A month the chosen year has none of is disabled rather than
              // hidden; if it was the one selected, the parent drops it.
              <option key={m.value} value={m.value} disabled={m.count === 0}>
                {m.label} ({m.count})
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
